import React from 'react';
import { useTranslation } from 'react-i18next';
import Users from '@/icons/Users';
import MessageSquare from '@/icons/MessageSquare';
import ImageIcon from '@/icons/Image';
import FileText from '@/icons/FileText';
import NotebookTabs from '@/icons/NotebookTabs';
import Sparkles from '@/icons/Sparkles';

export interface OverviewStats {
  memberCount: number;
  memoryCount: number | null;
  photoCount: number | null;
  sourceCount: number | null;
  /** When > 0 and user can review, show Review tile. */
  reviewCount?: number | null;
  /** When > 0 and user can author suggestions, show Suggestions tile. */
  suggestionCount?: number | null;
}

interface OverviewStatsGridProps {
  stats: OverviewStats;
  onMembers: () => void;
  onMemories: () => void;
  onPhotos: () => void;
  onSources: () => void;
  onReview?: () => void;
  onSuggestions?: () => void;
  membersDisabled?: boolean;
}

const formatCount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString();
};

const OverviewStatsGrid: React.FC<OverviewStatsGridProps> = ({
  stats,
  onMembers,
  onMemories,
  onPhotos,
  onSources,
  onReview,
  onSuggestions,
  membersDisabled = false
}) => {
  const { t } = useTranslation('overview');
  const showReview = typeof stats.reviewCount === 'number' && stats.reviewCount > 0 && !!onReview;
  const showSuggestions = typeof stats.suggestionCount === 'number' && stats.suggestionCount > 0 && !!onSuggestions;
  const tileCount = 4 + (showReview ? 1 : 0) + (showSuggestions ? 1 : 0);

  return (
    <div
      className={`overview-stats-grid${tileCount > 4 ? ' overview-stats-grid--dense' : ''}`}
      data-tile-count={tileCount}
    >
      <button
        type="button"
        className="stat-tile stat-tile--members"
        onClick={onMembers}
        disabled={membersDisabled}
        title={membersDisabled ? t('stats.membersDisabledTitle') : undefined}
      >
        <span className="stat-tile__icon">
          <Users width={22} height={22} />
        </span>
        <div className="stat-tile__value">{formatCount(stats.memberCount)}</div>
        <div className="stat-tile__label">
          <span className="stat-tile__label-full">{t('stats.membersFull')}</span>
          <span className="stat-tile__label-short" aria-hidden="true">
            {t('stats.membersShort')}
          </span>
        </div>
      </button>
      <button type="button" className="stat-tile stat-tile--memories" onClick={onMemories}>
        <span className="stat-tile__icon">
          <MessageSquare width={22} height={22} />
        </span>
        <div className="stat-tile__value">{formatCount(stats.memoryCount)}</div>
        <div className="stat-tile__label">{t('stats.memories')}</div>
      </button>
      <button type="button" className="stat-tile stat-tile--photos" onClick={onPhotos}>
        <span className="stat-tile__icon">
          <ImageIcon width={22} height={22} />
        </span>
        <div className="stat-tile__value">{formatCount(stats.photoCount)}</div>
        <div className="stat-tile__label">{t('stats.photos')}</div>
      </button>
      <button type="button" className="stat-tile stat-tile--sources" onClick={onSources}>
        <span className="stat-tile__icon">
          <FileText width={22} height={22} />
        </span>
        <div className="stat-tile__value">{formatCount(stats.sourceCount)}</div>
        <div className="stat-tile__label">{t('stats.sources')}</div>
      </button>
      {showReview ? (
        <button type="button" className="stat-tile stat-tile--review" onClick={onReview}>
          <span className="stat-tile__icon">
            <NotebookTabs width={22} height={22} />
          </span>
          <div className="stat-tile__value">{formatCount(stats.reviewCount)}</div>
          <div className="stat-tile__label">{t('stats.review')}</div>
        </button>
      ) : null}
      {showSuggestions ? (
        <button type="button" className="stat-tile stat-tile--suggestions" onClick={onSuggestions}>
          <span className="stat-tile__icon">
            <Sparkles width={22} height={22} />
          </span>
          <div className="stat-tile__value">{formatCount(stats.suggestionCount)}</div>
          <div className="stat-tile__label">{t('stats.suggestions')}</div>
        </button>
      ) : null}
    </div>
  );
};

export default OverviewStatsGrid;
